import { Injectable, inject } from '@angular/core';
import { HotToastService } from '@ngxpert/hot-toast';
import type { ToastPosition } from '@ngxpert/hot-toast';

export type NotificationSeverity = 'success' | 'error' | 'info' | 'warn';

export type NotificationPosition = ToastPosition;

/** Per-toast overrides of the global defaults (bottom-right, 2s). */
export interface NotificationOptions {
  position?: NotificationPosition;
  /** Milliseconds the toast stays visible. */
  duration?: number;
  /** Show the close button. */
  dismissible?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly toast = inject(HotToastService);

  success(detail: string, summary: string = 'Success', options?: NotificationOptions): void {
    this.toast.success(this.content(summary, detail), this.options('success', options));
  }

  error(detail: string, summary: string = 'Error', options?: NotificationOptions): void {
    this.toast.error(this.content(summary, detail), this.options('error', options));
  }

  info(detail: string, summary: string = 'Info', options?: NotificationOptions): void {
    this.toast.info(this.content(summary, detail), this.options('info', options));
  }

  warn(detail: string, summary: string = 'Warning', options?: NotificationOptions): void {
    this.toast.warning(this.content(summary, detail), this.options('warn', options));
  }

  clear(): void {
    this.toast.close();
  }

  clearByKey(_key: string): void {
    this.toast.close();
  }

  private options(
    severity: NotificationSeverity,
    overrides?: NotificationOptions
  ): { className: string; iconTheme: { primary: string; secondary: string } } & NotificationOptions {
    const varName = severity === 'warn' ? 'warning' : severity;
    return {
      className: `onrsr-toast onrsr-toast-${severity}`,
      iconTheme: { primary: `var(--notification-${varName}-color)`, secondary: '#ffffff' },
      ...overrides,
    };
  }

  private content(summary: string, detail: string): string {
    return `<div class="onrsr-toast-text"><span class="onrsr-toast-summary">${this.escape(summary)}</span><span class="onrsr-toast-detail">${this.escape(detail)}</span></div>`;
  }

  private escape(text: string): string {
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
}
